import { usePromotions } from "../context/PromotionContext";
import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { BsBasket } from "react-icons/bs";
import { BiFilterAlt } from "react-icons/bi";
import axios from "axios";
import { useMediaQuery } from "react-responsive";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CartContext } from "../context/CartContext";
import {
  extractFiltersFromProducts,
  filterProductsByCategory,
  filterProductsBySearch,
  getCategoriesFromProducts,
  getProductFilterValues,
  normalizeFilters,
  productMatchesCategory,
  pruneFiltersForProducts,
} from "../utils/filterUtils";
import FilterSidebar from "../components/FilterSidebar";
import Breadcrumbs from "../components/Breadcrumbs";
import ProductRatingSummary from "../components/ProductRatingSummary";
import DiscountBadge from "../components/DiscountBadge";
import { FaHeart } from "react-icons/fa";
import { PiHeart, PiScales } from "react-icons/pi";
import { useWishlist } from "../context/WishlistContext";
import { useComparison } from "../context/ComparisonContext";
import { CATEGORY_BUTTONS } from "../config/animalsConfig";
import {
  PROMOTIONS_PATH,
  getCategoryByPath,
  getCategoryByQuery,
  getCategoryBySlug,
} from "../config/navigation";
import { buildItemListSchema } from "../config/seo";
import {
  getProductAvailableQuantity,
  isProductInStock,
} from "../utils/productAvailability";
import {
  buildBrandPath,
  buildProductDetailPath,
  slugifyBrand,
} from "../utils/productLinks";
import { getProductImage } from "../utils/productImages";
import { getProductDisplayName } from "../utils/productDisplayName";
import {
  compareKindLabels,
  getKindPresentation,
} from "../utils/categoryKindPresentation";
import {
  buildCategoryPath,
  buildCategoryKindPath,
  buildPromotionCategoryPath,
  buildPromotionCategoryKindPath,
  findKindBySlug,
} from "../utils/categoryKindRoutes";
import {
  compareProductsWithStockPriority,
  DEFAULT_PRODUCT_SORT_KEY,
  PRODUCT_SORT_KEYS,
  getProductDiscountPrice,
  getProductDiscountPercent,
  getProductEffectivePrice,
  getProductRegularPrice,
  normalizeProductSortKey,
  stableSortProducts,
} from "../utils/productSorting";
import styles from "./Products.module.css";

/* ============================ Константи ============================ */

const ALL_ANIMALS_BUTTON = {
  label: "Для всіх",
  image:
    "https://dl.dropboxusercontent.com/scl/fi/sukn61aykynao61ecqnpl/ChatGPT-Image-25-.-2026-.-16_07_46.png?rlkey=c3m3nm60wphiff6uo27pl58g7&st=u6o9au70&dl=0",
  query: null,
};

const defaultFilters = {
  categories: [],
  kinds: [],
  types: [],
  purposes: [],
  manufacturers: [],
  countries: [],
  weights: [],
  priceRange: { min: 1, max: 10000 },
  feedClasses: [], // ["економ","преміум","суперпреміум","холістик"]
  animalAges: [], // ["до 12 місяців","1–7 років","7+ років","для всіх вікових груп"]
  breeds: [], // ["довгошерсті","короткошерсті", ...]
  mainIngredients: [], // ["курка","лосось","яловичина", ...]
  forSterilized: null, // null = будь-які, true = тільки для стерилізованих, false = не для стерилізованих
  hypoallergenic: null, // null = будь-які, true/false = фільтр
  therapeuticFeed: null,
};

const DISCOUNT_FILTER_PARAM = "discounted";
const LEGACY_DISCOUNT_SORT_KEY = "only-discounted";
const PROMOTION_PATHS = [PROMOTIONS_PATH, "/sale"];

const isPromotionPath = (pathname = "") =>
  PROMOTION_PATHS.includes(pathname) ||
  pathname.startsWith(`${PROMOTIONS_PATH}/`);

const isTruthyParamValue = (value) => {
  if (value == null) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes";
};

const isDiscountOnlyModeFromParams = (params) =>
  isTruthyParamValue(params.get(DISCOUNT_FILTER_PARAM)) ||
  String(params.get("sort") || "").trim() === LEGACY_DISCOUNT_SORT_KEY;

const buildKindBreadcrumbPath = ({
  activeCategoryItem,
  animalCategory,
  isPromotionMode = false,
  kinds,
}) => {
  if (activeCategoryItem?.slug && kinds.length === 1) {
    return isPromotionMode
      ? buildPromotionCategoryKindPath(activeCategoryItem.slug, kinds[0])
      : buildCategoryKindPath(activeCategoryItem.slug, kinds[0]);
  }

  const params = new URLSearchParams();

  if (!activeCategoryItem?.path && animalCategory) {
    params.set("category", animalCategory);
  }

  kinds.forEach((kind) => params.append("kind", kind));

  const search = params.toString();
  const basePath =
    isPromotionMode && activeCategoryItem?.slug
      ? buildPromotionCategoryPath(activeCategoryItem.slug)
      : activeCategoryItem?.path ||
        (isPromotionMode ? PROMOTIONS_PATH : "/products");

  return `${basePath}${search ? `?${search}` : ""}`;
};

const lowercaseFirst = (value = "") =>
  value ? value.charAt(0).toLocaleLowerCase("uk-UA") + value.slice(1) : "";

const getCategoryTargetPhrase = (categoryItem) =>
  lowercaseFirst(categoryItem?.query || "");

const formatCategoryHeading = (categoryItem) => {
  const targetPhrase = getCategoryTargetPhrase(categoryItem);

  return targetPhrase ? `Товари ${targetPhrase}` : "";
};

const formatPromotionCategoryHeading = (categoryItem) => {
  const targetPhrase = getCategoryTargetPhrase(categoryItem);

  return targetPhrase ? `Акційні товари ${targetPhrase}` : "Акційні товари";
};

const formatCategoryBreadcrumbLabel = (categoryItem) =>
  categoryItem?.query || categoryItem?.label || categoryItem?.menuLabel || "";

const formatKindHeading = (kind, categoryItem) => {
  const kindLabel = String(kind || "").trim();
  const targetPhrase = getCategoryTargetPhrase(categoryItem);

  if (!kindLabel) return categoryItem?.query || "";
  if (!targetPhrase) return kindLabel;

  return kindLabel.toLocaleLowerCase("uk-UA").includes(targetPhrase)
    ? kindLabel
    : `${kindLabel} ${targetPhrase}`;
};

const uniqueNonEmptyValues = (values = []) =>
  Array.from(
    new Set(values.map((value) => String(value || "").trim()).filter(Boolean)),
  );

const productMatchesManufacturers = (product, manufacturers = []) => {
  const selectedManufacturers = uniqueNonEmptyValues(manufacturers);
  if (!selectedManufacturers.length) return true;

  const productManufacturers = getProductFilterValues(
    product,
    "manufacturers",
  );
  const normalizedProductManufacturers = productManufacturers.map((value) => ({
    value: String(value || "").trim().toLowerCase(),
    slug: slugifyBrand(value),
  }));

  return selectedManufacturers.some((manufacturer) => {
    const normalizedManufacturer = manufacturer.toLowerCase();
    const manufacturerSlug = slugifyBrand(manufacturer);

    return normalizedProductManufacturers.some(
      (productManufacturer) =>
        productManufacturer.value === normalizedManufacturer ||
        productManufacturer.slug === manufacturerSlug,
    );
  });
};

/* ============================ Компонент ============================ */

const Products = () => {
  const { revision: promotionRevision } = usePromotions();
  const { addToCart } = useContext(CartContext);
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { isInComparison, toggleComparison } = useComparison();
  const { categorySlug, kindSlug, brandSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]); // всі товари
  const [filteredProducts, setFilteredProducts] = useState([]); // після фільтрів/пошуку
  const [filters, setFilters] = useState(defaultFilters);
  const safeFilters = useMemo(() => normalizeFilters(filters), [filters]); // ✅ тепер безпечно
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasSyncedInitialFilters, setHasSyncedInitialFilters] = useState(false);
  const [sortOption, setSortOption] = useState(DEFAULT_PRODUCT_SORT_KEY);
  const [animalCategory, setAnimalCategory] = useState(null);
  const [categorySelectedByAnimal, setCategorySelectedByAnimal] =
    useState(false);
  const [isProductsLoading, setIsProductsLoading] = useState(true);

  const productsPerPage = 20;
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const routeCategoryItem = useMemo(
    () => getCategoryBySlug(categorySlug) || getCategoryByPath(location.pathname),
    [categorySlug, location.pathname],
  );
  const routeCategory = routeCategoryItem?.query || null;
  const routeKind = useMemo(() => {
    if (!kindSlug || !routeCategory) return null;

    const categoryProducts = filterProductsByCategory(products, routeCategory);
    const routeKinds = new Set();

    categoryProducts.forEach((product) => {
      getProductFilterValues(product, "kinds").forEach((kind) => {
        routeKinds.add(kind);
      });
    });

    return findKindBySlug(Array.from(routeKinds), kindSlug);
  }, [kindSlug, products, routeCategory]);
  const routeManufacturer = useMemo(() => {
    if (!brandSlug) return null;

    const decodedBrand = decodeURIComponent(brandSlug).trim();
    if (!decodedBrand) return null;

    const hyphenAsSpaceBrand = decodedBrand.replace(/-/g, " ");
    const manufacturers = Array.from(
      new Set(
        products.flatMap((product) =>
          getProductFilterValues(product, "manufacturers"),
        ),
      ),
    );
    const normalizedRouteBrand = decodedBrand.toLowerCase();
    const normalizedSpacedRouteBrand = hyphenAsSpaceBrand.toLowerCase();

    return (
      manufacturers.find((manufacturer) => {
        const value = String(manufacturer || "").trim();
        const normalizedValue = value.toLowerCase();

        return (
          normalizedValue === normalizedRouteBrand ||
          normalizedValue === normalizedSpacedRouteBrand ||
          slugifyBrand(value) === normalizedRouteBrand ||
          encodeURIComponent(value).toLowerCase() === normalizedRouteBrand
        );
      }) || decodedBrand
    );
  }, [brandSlug, products]);
  const isDiscountOnlyMode = useMemo(
    () =>
      isPromotionPath(location.pathname) ||
      isDiscountOnlyModeFromParams(searchParams),
    [location.pathname, searchParams],
  );
  const queryManufacturer =
    searchParams.get("manufacturer") || searchParams.get("brand");
  const activeManufacturerFilters = useMemo(
    () =>
      uniqueNonEmptyValues([
        ...safeFilters.manufacturers,
        routeManufacturer,
        queryManufacturer,
      ]),
    [queryManufacturer, routeManufacturer, safeFilters.manufacturers],
  );
  const activeBrandManufacturer = activeManufacturerFilters[0] || "";
  const isBrandFilterActive = activeManufacturerFilters.length > 0;

  // Якщо env не задано — падаємо на localhost:5000
  const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  /* ============================ Хелпери ============================ */

  // Безпечно нормалізуємо відповідь бекенду до масиву
  const toArray = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.items)) return data.items;
    if (data && Array.isArray(data.products)) return data.products;
    return [];
  };

  // Пагінація (плаваючі крапки)
  const getPageNumbers = () => {
    const safeFiltered = Array.isArray(filteredProducts)
      ? filteredProducts
      : [];
    const totalPages = Math.max(
      1,
      Math.ceil(safeFiltered.length / productsPerPage)
    );
    const range = [];
    const delta = 2;
    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    range.push(1);
    if (left > 2) range.push("dots");
    for (let i = left; i <= right; i++) range.push(i);
    if (right < totalPages - 1) range.push("dots");
    if (totalPages > 1) range.push(totalPages);

    return range;
  };

  /* ============================ URL sync ============================ */

useEffect(() => {
  const queryFromURL = searchParams.get("q") || "";
  const sortFromURL = searchParams.get("sort");
  const hasLegacyDiscountSort =
    String(sortFromURL || "").trim() === LEGACY_DISCOUNT_SORT_KEY;
  const hasDiscountFilter = isTruthyParamValue(
    searchParams.get(DISCOUNT_FILTER_PARAM),
  );

  setSearchQuery(queryFromURL);
  setSortOption(normalizeProductSortKey(sortFromURL));

  if (hasLegacyDiscountSort && !hasDiscountFilter) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(DISCOUNT_FILTER_PARAM, "1");
    nextParams.delete("sort");
    setSearchParams(nextParams, { replace: true });
  }
}, [searchParams, setSearchParams]);


  useEffect(() => {
    const categoryFromURL = searchParams.get("category") || routeCategory;
    const kindsFromURL =
      routeKind ? [routeKind] : searchParams.getAll("kind").filter(Boolean);
    const manufacturerFromURL =
      routeManufacturer ||
      searchParams.get("manufacturer") ||
      searchParams.get("brand");

    if (manufacturerFromURL) {
      setFilters((prev) => ({
        ...prev,
        manufacturers: [manufacturerFromURL],
      }));
    } else {
      setFilters((prev) =>
        prev.manufacturers?.length
          ? {
              ...prev,
              manufacturers: [],
            }
          : prev,
      );
    }

    if (categoryFromURL) {
      setAnimalCategory(categoryFromURL);
      setCategorySelectedByAnimal(
        CATEGORY_BUTTONS.some((item) => item.query === categoryFromURL)
      );
      setFilters((prev) =>
        pruneFiltersForProducts(
          {
            ...prev,
            categories: [categoryFromURL],
            kinds: kindsFromURL,
          },
          products,
          categoryFromURL
        )
      );
    } else if (categorySelectedByAnimal) {
      setAnimalCategory(null);
      setCategorySelectedByAnimal(false);
      setFilters((prev) => normalizeFilters({ ...prev, categories: [] }));
    } else if (kindsFromURL.length > 0) {
      setFilters((prev) => normalizeFilters({ ...prev, kinds: kindsFromURL }));
    }
  }, [
    categorySelectedByAnimal,
    routeCategory,
    routeKind,
    routeManufacturer,
    searchParams,
    products,
  ]);

  /* ============================ Завантаження товарів ============================ */

  useEffect(() => {
    let isCancelled = false;

    const fetchProducts = async () => {
      setIsProductsLoading(true);

      try {
        // Публічний ендпоїнт (без токена)
        const res = await axios.get(`${backendUrl}/api/products`, {
          params: { limit: 1200 },
          withCredentials: false,
        });

        if (isCancelled) return;

        const data = toArray(res.data);
        setProducts(data);
        setFilteredProducts(data);
      } catch (err) {
        if (isCancelled) return;

        console.error("Помилка при завантаженні продуктів:", err);
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        if (!isCancelled) {
          setIsProductsLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      isCancelled = true;
    };
  }, [backendUrl, promotionRevision]);

  const searchScopedProducts = useMemo(
    () => {
      const catalogSource = isDiscountOnlyMode
        ? products.filter(
            (product) =>
              getProductDiscountPercent(product) > 0 &&
              isProductInStock(product),
          )
        : products;

      return filterProductsBySearch(catalogSource, searchQuery);
    },
    [isDiscountOnlyMode, products, searchQuery],
  );

  const filtersSourceProducts = useMemo(() => {
    const categoryScopedProducts = filterProductsByCategory(
      searchScopedProducts,
      animalCategory,
    );

    if (!isBrandFilterActive) return categoryScopedProducts;

    return categoryScopedProducts.filter((product) =>
      productMatchesManufacturers(product, activeManufacturerFilters),
    );
  }, [
    activeManufacturerFilters,
    animalCategory,
    isBrandFilterActive,
    searchScopedProducts,
  ]);

  const searchRelevantCategories = useMemo(
    () => getCategoriesFromProducts(filtersSourceProducts),
    [filtersSourceProducts]
  );

  /* ============================ Фільтр по тваринах ============================ */

  const handleAnimalFilter = useCallback(
    (item) => {
      const category = item?.query || null;

      setSearchQuery("");

      const newParams = new URLSearchParams(searchParams);
      newParams.delete("q");
      newParams.delete("category");
      newParams.delete("kind");
      newParams.delete("brand");
      newParams.delete(DISCOUNT_FILTER_PARAM);
      if (String(newParams.get("sort") || "").trim() === LEGACY_DISCOUNT_SORT_KEY) {
        newParams.delete("sort");
      }
      if (isBrandFilterActive && activeBrandManufacturer) {
        newParams.set("manufacturer", activeBrandManufacturer);
      }

      const nextSearch = newParams.toString();
      let nextPath = "/products";

      if (isDiscountOnlyMode) {
        nextPath = PROMOTIONS_PATH;
        if (category && item?.slug) {
          nextPath = buildPromotionCategoryPath(item.slug);
        }
      } else if (category && item?.slug) {
        nextPath = buildCategoryPath(item.slug);
      }

      setAnimalCategory(category);
      setCategorySelectedByAnimal(Boolean(category));

      setFilters((prev) =>
        pruneFiltersForProducts(
          { ...prev, categories: category ? [category] : [], kinds: [] },
          products,
          category
        )
      );

      navigate(`${nextPath}${nextSearch ? `?${nextSearch}` : ""}`);
    },
    [
      activeBrandManufacturer,
      isBrandFilterActive,
      isDiscountOnlyMode,
      navigate,
      products,
      searchParams,
    ]
  );

  const handleRemoveCategoryTag = useCallback(
    (category) => {
      const nextAnimalCategory =
        category === animalCategory ? null : animalCategory;
      const newParams = new URLSearchParams(searchParams);

      if (category === animalCategory || newParams.get("category") === category) {
        newParams.delete("category");
      }

      setAnimalCategory(nextAnimalCategory);
      setCategorySelectedByAnimal(false);
      setFilters((prev) =>
        pruneFiltersForProducts(
          {
            ...prev,
            categories: (prev.categories || []).filter((c) => c !== category),
          },
          products,
          nextAnimalCategory
        )
      );
      if (routeCategory === category) {
        const nextSearch = newParams.toString();
        navigate(`/products${nextSearch ? `?${nextSearch}` : ""}`);
      } else {
        setSearchParams(newParams);
      }
    },
    [animalCategory, navigate, products, routeCategory, searchParams, setSearchParams]
  );

  const handleFilterChange = useCallback(
    (newFilters) => {
      if (
        Object.prototype.hasOwnProperty.call(newFilters, "categories")
      ) {
        const nextCategories = normalizeFilters({
          categories: newFilters.categories,
        }).categories;
        const nextAnimalCategory =
          animalCategory && nextCategories.includes(animalCategory)
            ? animalCategory
            : null;
        const newParams = new URLSearchParams(searchParams);

        if (nextAnimalCategory) {
          newParams.set("category", nextAnimalCategory);
        } else {
          newParams.delete("category");
        }

        setAnimalCategory(nextAnimalCategory);
        setCategorySelectedByAnimal(false);
        setFilters((prev) =>
          pruneFiltersForProducts(
            { ...prev, ...newFilters, categories: nextCategories },
            products,
            nextAnimalCategory
          )
        );

        if (routeCategory && !nextAnimalCategory) {
          const nextSearch = newParams.toString();
          navigate(`/products${nextSearch ? `?${nextSearch}` : ""}`);
        } else {
          setSearchParams(newParams);
        }
        return;
      }

      if (Object.prototype.hasOwnProperty.call(newFilters, "kinds")) {
        const nextKinds = normalizeFilters({
          kinds: newFilters.kinds,
        }).kinds;
        const newParams = new URLSearchParams(searchParams);

        newParams.delete("kind");
        nextKinds.forEach((kind) => newParams.append("kind", kind));

        setFilters((prev) =>
          normalizeFilters({ ...prev, ...newFilters, kinds: nextKinds })
        );
        setSearchParams(newParams);
        return;
      }

      setFilters((prev) => normalizeFilters({ ...prev, ...newFilters }));
    },
    [animalCategory, navigate, products, routeCategory, searchParams, setSearchParams]
  );

  const handleResetFilters = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("category");
    newParams.delete("kind");
    newParams.delete("manufacturer");
    newParams.delete("brand");

    setAnimalCategory(null);
    setCategorySelectedByAnimal(false);
    setFilters({ ...defaultFilters });

    if (routeCategory || routeManufacturer) {
      const nextSearch = newParams.toString();
      navigate(`/products${nextSearch ? `?${nextSearch}` : ""}`);
    } else {
      setSearchParams(newParams);
    }
  }, [navigate, routeCategory, routeManufacturer, searchParams, setSearchParams]);

  /* ============================ Застосування фільтрів/сорту ============================ */

  const applyFilters = useCallback(() => {
    // Безпечне копіювання масиву товарів
    let temp = Array.isArray(searchScopedProducts)
      ? [...searchScopedProducts]
      : [];

    // 🐾 Фільтр за твариною (категорія)
    if (animalCategory) {
      temp = temp.filter((product) =>
        productMatchesCategory(product, animalCategory)
      );
    }

    // 📦 Фільтр за категоріями
    if (safeFilters.categories.length > 0) {
      temp = temp.filter((product) =>
        safeFilters.categories.some((category) =>
          productMatchesCategory(product, category)
        )
      );
    }

    // 🧩 Вид
    if (safeFilters.kinds.length > 0) {
      temp = temp.filter((product) =>
        getProductFilterValues(product, "kinds").some((kind) =>
          safeFilters.kinds.includes(kind)
        )
      );
    }

    // 🧩 Тип
    if (safeFilters.types.length > 0) {
      temp = temp.filter((p) => safeFilters.types.includes(p.type));
    }

    // 🧩 Призначення
    if (safeFilters.purposes.length > 0) {
      temp = temp.filter((p) =>
        getProductFilterValues(p, "purposes").some((value) =>
          safeFilters.purposes.includes(value)
        )
      );
    }

    // 🏭 Виробник
    if (safeFilters.manufacturers.length > 0) {
      temp = temp.filter((product) =>
        productMatchesManufacturers(product, safeFilters.manufacturers)
      );
    }

    // 🌍 Країна-виробник
    if (safeFilters.countries.length > 0) {
      temp = temp.filter((p) =>
        safeFilters.countries.includes(p.countryOfOrigin)
      );
    }

    // ⚖️ Вага
    if (safeFilters.weights.length > 0) {
      temp = temp.filter((p) => safeFilters.weights.includes(p.weight));
    }

    // 💰 Діапазон цін
    if (safeFilters.priceRange) {
      const { min, max } = safeFilters.priceRange;
      temp = temp.filter(
        (p) =>
          Number(p.price) >= Number(min || 1) &&
          Number(p.price) <= Number(max || 10000)
      );
    }

    // 🔍 Пошук по назві
    /* ==========================================
     🟢 ДОДАНІ ПОЛЯ ДЛЯ КОРМІВ
    ========================================== */

    // 🏷️ Клас корму
    if (safeFilters.feedClasses.length > 0) {
      temp = temp.filter((p) => safeFilters.feedClasses.includes(p.feedClass));
    }

    // 🐾 Вік тварини
    if (safeFilters.animalAges.length > 0) {
      temp = temp.filter((p) =>
        getProductFilterValues(p, "animalAges").some((value) =>
          safeFilters.animalAges.includes(value)
        )
      );
    }

    // 🐶 Порода (масив або рядок)
    if (safeFilters.breeds.length > 0) {
      temp = temp.filter((p) => {
        const list = Array.isArray(p.breed)
          ? p.breed
          : p.breed
          ? [p.breed]
          : [];
        return list.some((b) => safeFilters.breeds.includes(b));
      });
    }

    // 🍗 Основні інгредієнти (масив або рядок)
    if (safeFilters.mainIngredients.length > 0) {
      temp = temp.filter((p) => {
        const list = Array.isArray(p.mainIngredients)
          ? p.mainIngredients
          : p.mainIngredients
          ? [p.mainIngredients]
          : [];
        return list.some((ing) => safeFilters.mainIngredients.includes(ing));
      });
    }

    // ⚕️ Для стерилізованих (булевий)
    if (safeFilters.forSterilized !== null) {
      temp = temp.filter((p) => p.forSterilized === safeFilters.forSterilized);
    }

    // 🌿 Гіпоалергенний (булевий)
    if (safeFilters.hypoallergenic !== null) {
      temp = temp.filter(
        (p) => p.hypoallergenic === safeFilters.hypoallergenic
      );
    }

    if (safeFilters.therapeuticFeed !== null) {
      temp = temp.filter(p => p.therapeuticFeed === safeFilters.therapeuticFeed);
    }

    /* ==========================================
     🔁 Сортування
    ========================================== */

    const normalizedSortKey = normalizeProductSortKey(sortOption);
    temp = stableSortProducts(temp, (a, b) =>
      compareProductsWithStockPriority(a, b, normalizedSortKey),
    );

    /* ==========================================
     🔚 Збереження результату
    ========================================== */

    setFilteredProducts(temp);
    setCurrentPage(1);
  }, [
    searchScopedProducts,
    safeFilters,
    sortOption,
    animalCategory,
  ]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters, filters]);

  // Після введення пошуку — підкинути фільтри з продуктів
  useEffect(() => {
    if (!hasSyncedInitialFilters && searchQuery) {
      const extracted = extractFiltersFromProducts(
        Array.isArray(filteredProducts) ? filteredProducts : []
      );
      setFilters((prev) => ({ ...defaultFilters, ...extracted }));
      setHasSyncedInitialFilters(true);
    }
  }, [searchQuery, filteredProducts, hasSyncedInitialFilters]);

  /* ============================ Похідні значення для рендера ============================ */

  const safeFiltered = useMemo(
    () => (Array.isArray(filteredProducts) ? filteredProducts : []),
    [filteredProducts],
  );
  const totalPages = Math.max(
    1,
    Math.ceil(safeFiltered.length / productsPerPage)
  );
  const indexOfLast = currentPage * productsPerPage;
  const indexOfFirst = indexOfLast - productsPerPage;
  const currentProducts = safeFiltered.slice(indexOfFirst, indexOfLast);
  const activeCategoryItem = animalCategory
    ? getCategoryByQuery(animalCategory)
    : routeCategoryItem;
  const selectedKindFilters = safeFilters.kinds;
  const selectedKindBreadcrumbPath =
    selectedKindFilters.length > 0
      ? buildKindBreadcrumbPath({
          activeCategoryItem,
          animalCategory,
          isPromotionMode: isDiscountOnlyMode,
          kinds: selectedKindFilters,
        })
      : null;
  const catalogHeading = isDiscountOnlyMode
    ? formatPromotionCategoryHeading(activeCategoryItem)
    : activeCategoryItem
    ? activeCategoryItem.menuLabel
    : searchQuery
    ? `Пошук: ${searchQuery}`
    : "Каталог товарів";
  const animalFilterButtons = [...CATEGORY_BUTTONS, ALL_ANIMALS_BUTTON];
  const resultsCount = safeFiltered.length;
  const itemListSchema = buildItemListSchema({
    name: catalogHeading,
    items: currentProducts.filter((product) => product.slug),
  });
  const isCleanCategoryRoute = Boolean(
    activeCategoryItem && location.pathname === activeCategoryItem.path,
  );

  const kinds = useMemo(
    () => {
      const kindSet = new Set();

      filtersSourceProducts.forEach((product) => {
        getProductFilterValues(product, "kinds").forEach((kind) => {
          kindSet.add(kind);
        });
      });

      return Array.from(kindSet).sort(compareKindLabels);
    },
    [filtersSourceProducts]
  );

  const categoryTargetPhrase = getCategoryTargetPhrase(activeCategoryItem);
  const shouldRenderHeaderTitle =
    Boolean(activeCategoryItem) || isDiscountOnlyMode;
  const shouldRenderKindNav =
    kinds.length >= 2 &&
    (Boolean(activeCategoryItem) || !isDiscountOnlyMode || isBrandFilterActive);
  const shouldRenderCategoryHeader =
    shouldRenderHeaderTitle || shouldRenderKindNav;
  const kindNavAriaLabel = activeCategoryItem
    ? `Види товарів ${categoryTargetPhrase}`
    : "Види товарів";
  const visibleCategoryHeaderTitle =
    selectedKindFilters.length === 1 && activeCategoryItem
      ? isDiscountOnlyMode
        ? `${formatKindHeading(
            selectedKindFilters[0],
            activeCategoryItem,
          )}`
        : formatKindHeading(selectedKindFilters[0], activeCategoryItem)
    : isDiscountOnlyMode && activeCategoryItem
      ? formatPromotionCategoryHeading(activeCategoryItem)
      : isDiscountOnlyMode
      ? "Акційні товари"
      : activeCategoryItem
      ? formatCategoryHeading(activeCategoryItem)
      : catalogHeading;

  const categoryFilterBasePath = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    const brandBasePath =
      brandSlug && activeBrandManufacturer
        ? buildBrandPath(activeBrandManufacturer)
        : null;

    params.delete("kind");
    params.delete("brand");

    if (brandBasePath) {
      params.delete("manufacturer");
      if (activeCategoryItem?.query || animalCategory) {
        params.set("category", activeCategoryItem?.query || animalCategory);
      } else {
        params.delete("category");
      }
    } else if (isBrandFilterActive && activeBrandManufacturer) {
      params.set("manufacturer", activeBrandManufacturer);
    }

    if (isDiscountOnlyMode && !brandBasePath) {
      params.delete(DISCOUNT_FILTER_PARAM);
      if (String(params.get("sort") || "").trim() === LEGACY_DISCOUNT_SORT_KEY) {
        params.delete("sort");
      }
    }

    if (!brandBasePath && activeCategoryItem?.path) {
      params.delete("category");
    } else if (!brandBasePath && animalCategory) {
      params.set("category", animalCategory);
    }

    const search = params.toString();
    const basePath = brandBasePath
      ? brandBasePath
      : isDiscountOnlyMode && activeCategoryItem?.slug
        ? buildPromotionCategoryPath(activeCategoryItem.slug)
        : activeCategoryItem?.path || (isDiscountOnlyMode ? PROMOTIONS_PATH : "/products");

    return `${basePath}${search ? `?${search}` : ""}`;
  }, [
    activeBrandManufacturer,
    activeCategoryItem,
    animalCategory,
    brandSlug,
    isBrandFilterActive,
    isDiscountOnlyMode,
    searchParams,
  ]);

  const buildCategoryKindFilterPath = useCallback(
    (kind) => {
      const params = new URLSearchParams(searchParams);
      const brandBasePath =
        brandSlug && activeBrandManufacturer
          ? buildBrandPath(activeBrandManufacturer)
          : null;

      params.delete("kind");
      params.delete("brand");

      if (brandBasePath) {
        params.delete("manufacturer");
        params.append("kind", kind);

        if (activeCategoryItem?.query || animalCategory) {
          params.set("category", activeCategoryItem?.query || animalCategory);
        } else {
          params.delete("category");
        }

        const search = params.toString();

        return `${brandBasePath}${search ? `?${search}` : ""}`;
      }

      if (isBrandFilterActive && activeBrandManufacturer) {
        params.set("manufacturer", activeBrandManufacturer);
      }

      if (activeCategoryItem?.slug) {
        params.delete("category");
        if (isDiscountOnlyMode) {
          params.delete(DISCOUNT_FILTER_PARAM);
          if (String(params.get("sort") || "").trim() === LEGACY_DISCOUNT_SORT_KEY) {
            params.delete("sort");
          }
        }

        const search = params.toString();
        const basePath = isDiscountOnlyMode
          ? buildPromotionCategoryKindPath(activeCategoryItem.slug, kind)
          : buildCategoryKindPath(activeCategoryItem.slug, kind);

        return `${basePath}${search ? `?${search}` : ""}`;
      }

      if (isDiscountOnlyMode) {
        params.delete(DISCOUNT_FILTER_PARAM);
        if (String(params.get("sort") || "").trim() === LEGACY_DISCOUNT_SORT_KEY) {
          params.delete("sort");
        }
      }
      params.append("kind", kind);

      if (activeCategoryItem?.path) {
        params.delete("category");
      } else if (animalCategory) {
        params.set("category", animalCategory);
      }

      const search = params.toString();
      const basePath =
        isDiscountOnlyMode && activeCategoryItem?.slug
          ? buildPromotionCategoryPath(activeCategoryItem.slug)
          : activeCategoryItem?.path ||
            (isDiscountOnlyMode ? PROMOTIONS_PATH : "/products");

      return `${basePath}${search ? `?${search}` : ""}`;
    },
    [
      activeBrandManufacturer,
      activeCategoryItem,
      animalCategory,
      brandSlug,
      isBrandFilterActive,
      isDiscountOnlyMode,
      searchParams,
    ],
  );

  const categoryBreadcrumbPath = activeCategoryItem
    ? isDiscountOnlyMode
      ? buildPromotionCategoryPath(activeCategoryItem.slug)
      : activeCategoryItem.path
    : null;
  const selectedKindBreadcrumbLabel =
    isDiscountOnlyMode &&
    selectedKindFilters.length === 1 &&
    activeCategoryItem
      ? formatKindHeading(selectedKindFilters[0], activeCategoryItem)
      : selectedKindFilters;
  const breadcrumbPaths = [
    { label: "Головна", to: "/" },
    ...(isDiscountOnlyMode
      ? [{ label: "Акційні товари", to: PROMOTIONS_PATH }]
      : !activeCategoryItem && !isCleanCategoryRoute
      ? [{ label: "Каталог товарів", to: "/products" }]
      : []),
    ...(activeCategoryItem
      ? [
          {
            label: isDiscountOnlyMode
              ? formatCategoryBreadcrumbLabel(activeCategoryItem)
              : activeCategoryItem.menuLabel,
            to: categoryBreadcrumbPath,
          },
        ]
      : []),
    ...(selectedKindFilters.length > 0
      ? [
          {
            label: selectedKindBreadcrumbLabel,
            to: selectedKindBreadcrumbPath,
            clickable: true,
          },
        ]
      : []),
    ...(filters.manufacturers.length >= 1
      ? [
          {
            label: filters.manufacturers[0],
            to: buildBrandPath(filters.manufacturers[0]),
          },
        ]
      : []),
  ];
  const isCategoryListingPath =
    location.pathname.startsWith("/category/") ||
    location.pathname.startsWith(`${PROMOTIONS_PATH}/category/`);
  const isUnknownCategoryRoute = Boolean(
    categorySlug && isCategoryListingPath && !routeCategoryItem,
  );
  const isUnknownKindRoute = Boolean(
    kindSlug && routeCategoryItem && !isProductsLoading && !routeKind,
  );
  const selectedKindForProductLink =
    selectedKindFilters.length === 1 ? selectedKindFilters[0] : null;
  const productDetailLinkState = useMemo(() => {
    const hasCategoryContext = Boolean(activeCategoryItem?.slug);
    const hasBrandContext = Boolean(activeBrandManufacturer);
    const hasLinkContext =
      isDiscountOnlyMode || hasCategoryContext || hasBrandContext;

    if (!hasLinkContext) return undefined;

    return {
      ...(isDiscountOnlyMode
        ? { fromPromotions: true }
        : hasCategoryContext
        ? { fromCategory: true, fromPromotions: false }
        : {}),
      ...(hasCategoryContext ? { animalSlug: activeCategoryItem.slug } : {}),
      ...(selectedKindForProductLink ? { kind: selectedKindForProductLink } : {}),
      ...(hasBrandContext
        ? {
            fromBrand: true,
            brandSlug: brandSlug || slugifyBrand(activeBrandManufacturer),
          }
        : {}),
      sourcePath: `${location.pathname}${location.search}`,
    };
  }, [
    activeBrandManufacturer,
    activeCategoryItem,
    brandSlug,
    isDiscountOnlyMode,
    location.pathname,
    location.search,
    selectedKindForProductLink,
  ]);
  const buildProductCardDetailPath = useCallback(
    (product) =>
      buildProductDetailPath(product, {
        ...(activeCategoryItem?.query
          ? { category: activeCategoryItem.query }
          : {}),
        ...(selectedKindForProductLink ? { kind: selectedKindForProductLink } : {}),
      }),
    [activeCategoryItem, selectedKindForProductLink],
  );

  const getAvailableQuantity = getProductAvailableQuantity;

  const getDiscountPercent = (product) => {
    const percent = getProductDiscountPercent(product);
    if (percent <= 0) return null;
    return Math.round(percent);
  };

  const formatPrice = (value) =>
    new Intl.NumberFormat("uk-UA", {
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);

  const handleCatalogSearchChange = (event) => {
    const value = event.target.value;
    const newParams = new URLSearchParams(searchParams);

    setSearchQuery(value);
    if (value.trim()) {
      newParams.set("q", value);
    } else {
      newParams.delete("q");
    }

    setSearchParams(newParams, { replace: true });
  };

  const handleSortChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    const normalizedValue = normalizeProductSortKey(value);

    setSortOption(normalizedValue);
    if (normalizedValue !== DEFAULT_PRODUCT_SORT_KEY) {
      newParams.set("sort", normalizedValue);
    } else {
      newParams.delete("sort");
    }

    setSearchParams(newParams, { replace: true });
  };

  const renderSortSelect = (className = styles.sortSelect) => {
    return (
      <select
        id="sort"
        value={sortOption}
        onChange={(event) => handleSortChange(event.target.value)}
        className={className}
        aria-label="Сортування товарів"
      >
        <option value={PRODUCT_SORT_KEYS.POPULARITY}>За популярністю</option>
        <option value={PRODUCT_SORT_KEYS.RATING}>За рейтингом</option>
        <option value={PRODUCT_SORT_KEYS.DISCOUNT}>За розміром знижки</option>
        <option value={PRODUCT_SORT_KEYS.PRICE_ASC}>Від дешевих до дорогих</option>
        <option value={PRODUCT_SORT_KEYS.PRICE_DESC}>Від дорогих до дешевих</option>
      </select>
    );
  };

  const renderActiveFilters = () => {
    if (!filters.categories.length) return null;

    return (
      <div className={styles.activeFilters}>
        {filters.categories.map((cat) => (
          <span key={cat} className={styles.filterTag}>
            {cat}
            <button
              type="button"
              className={styles.removeTag}
              onClick={() => handleRemoveCategoryTag(cat)}
              aria-label={`Прибрати фільтр ${cat}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    );
  };

  const renderFiltersSummary = () => {
    return (
      <div className={styles.filtersSummaryRow}>
        {filters.categories.length > 0 && (
          <div className={styles.activeFiltersBlock}>{renderActiveFilters()}</div>
        )}
        <div className={styles.resultsCount} aria-live="polite">
          Знайдено товарів: {resultsCount}
        </div>
      </div>
    );
  };

  if (isUnknownCategoryRoute || isUnknownKindRoute) {
    const notFoundTitle = isUnknownCategoryRoute
      ? "Категорію не знайдено"
      : "Вид товарів не знайдено";

    return (
      <div className={styles.productsPage}>
        <Breadcrumbs
          paths={[
            { label: "Головна", to: "/" },
            { label: notFoundTitle, to: location.pathname },
          ]}
        />
        <section className={styles.categoryHeader}>
          <h1>{notFoundTitle}</h1>
        </section>
      </div>
    );
  }

  /* ============================ Рендер ============================ */

  return (
    <div className={styles.productsPage}>
      {itemListSchema.itemListElement.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(itemListSchema)}
          </script>
        </Helmet>
      )}

      <Breadcrumbs paths={breadcrumbPaths} />

      {successMessage && (
        <div className={styles.successMessage}>{successMessage}</div>
      )}

      {/* Animal filter */}
      <div className={styles.animalFilterScrollWrapper}>
        <div className={styles.animalFilterWrapper}>
          {animalFilterButtons.map((item) => {
            const isActive =
              item.query == null
                ? !animalCategory
                : animalCategory === item.query;

            const hasProducts =
              item.query == null
                ? true
                : searchScopedProducts.some((product) =>
                    productMatchesCategory(product, item.query)
                  );

            if (!hasProducts && searchQuery) {
              return (
                <button
                  key={item.label}
                  className={`${styles.animalButton} ${styles.disabledButton}`}
                  disabled
                >
                  <img
                    src={item.image}
                    alt={item.label}
                    className={styles.animalIcon}
                  />
                  <p>{item.label}</p>
                </button>
              );
            }

            return (
              <button
                key={item.label}
                className={`${styles.animalButton} ${
                  isActive ? styles.activeButton : ""
                }`}
                onClick={() => handleAnimalFilter(item)}
              >
                <img
                  src={item.image}
                  alt={item.label}
                  className={styles.animalIcon}
                />
                <p>{item.label}</p>
              </button>
            );
          })}
        </div>
      </div>

      {shouldRenderCategoryHeader && (
        <section className={styles.categoryHeader}>
          {shouldRenderHeaderTitle && (
            <h1 id="catalog-heading">{visibleCategoryHeaderTitle}</h1>
          )}
          {shouldRenderKindNav && (
            <nav
              className={styles.categoryKindNav}
              aria-label={kindNavAriaLabel}
            >
              {kinds.map((kind) => {
                const isActive = selectedKindFilters.includes(kind);
                const kindPresentation = getKindPresentation(kind);
                const to =
                  isActive && selectedKindFilters.length === 1
                    ? categoryFilterBasePath
                    : buildCategoryKindFilterPath(kind);

                return (
                  <Link
                    key={kind}
                    to={to}
                    className={`${styles.categoryKindNavItem} ${
                      isActive ? styles.categoryKindNavItemActive : ""
                    }`}
                  >
                    <img
                      src={kindPresentation.image}
                      alt=""
                      aria-hidden="true"
                      className={styles.categoryKindNavIcon}
                      loading="lazy"
                    />
                    <span>{kind}</span>
                  </Link>
                );
              })}
            </nav>
          )}
        </section>
      )}

      {/* Десктоп toolbar */}
      {!isMobile && (
        <>
          <div className={styles.catalogToolbar}>
            <div className={styles.searchField}>
              <input
                type="search"
                value={searchQuery}
                onChange={handleCatalogSearchChange}
                className={styles.searchInput}
                placeholder="Пошук товарів для ваших улюбленців..."
                aria-label="Пошук товарів"
              />
            </div>

            <div className={styles.sortField}>
              {renderSortSelect()}
            </div>
          </div>

          {renderFiltersSummary()}
        </>
      )}

      {/* Мобільний toolbar */}
      {isMobile && (
        <>
          <div className={styles.mobileCatalogToolbar}>
            <button
              type="button"
              className={styles.mobileFilterButton}
              onClick={() => setIsFilterSidebarOpen(true)}
            >
              <BiFilterAlt className={styles.iconLarge} />
              Фільтр
            </button>
            <div className={styles.mobileSortField}>
              {renderSortSelect(styles.mobileSortSelect)}
            </div>
          </div>
          {renderFiltersSummary()}
        </>
      )}

      <div className={styles.mainContentWrapper}>
        {/* Desktop sidebar */}
        {!isMobile && (
          <div className={styles.desktopSidebarWrapper}>
            <div className={styles.scrollableSidebar}>
              <FilterSidebar
                isOpen={isFilterSidebarOpen}
                onClose={() => setIsFilterSidebarOpen(false)}
                products={filtersSourceProducts}
                kinds={kinds}
                filters={filters}
                animalCategory={animalCategory}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                visibleCategories={searchRelevantCategories}
              />
            </div>
          </div>
        )}

        {/* Cards */}
        <div className={styles.productsContainerWithPagination}>
          <div className={styles.productsContainer}>
            {isProductsLoading ? (
              <div className={styles.loadingState} role="status" aria-live="polite">
                <span className={styles.loadingSpinner} aria-hidden="true" />
                <p className={styles.loadingText}>Завантажуємо товари для вас...</p>
              </div>
            ) : currentProducts.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateEmoji} aria-hidden="true">
                  😟
                </div>
                <h3>{isDiscountOnlyMode ? "Наразі чинних акційних пропозицій немає" : "Наразі товарів за цими умовами немає"}</h3><p>Спробуйте інші фільтри або перегляньте весь каталог.</p><Link to="/products">Перейти до каталогу</Link>
              </div>
            ) : (
              currentProducts.map((product) => {
              const available = getAvailableQuantity(product);
              const isOutOfStock = available <= 0;
              const discountPercent = getDiscountPercent(product);
              const regularPrice = getProductRegularPrice(product);
              const discountPrice = getProductDiscountPrice(product);
              const currentPrice = getProductEffectivePrice(product);

              return (
                <article key={product._id} className={styles.productCard}>
                  <Link
                    to={buildProductCardDetailPath(product)}
                    state={productDetailLinkState}
                    className={styles.productLink}
                    aria-label={product.name}
                  >
                    <div className={styles.productCardImageWrap}>
                      <img
                        src={getProductImage(product, "thumbnail")}
                        alt={product.name}
                        className={`${styles.productImage} ${
                          isOutOfStock ? styles.mutedProductImage : ""
                        }`}
                        loading="lazy"
                        decoding="async"
                        onError={(event) => {
                          event.currentTarget.src =
                            "/images/default-product.jpg";
                        }}
                      />

                      {!isOutOfStock && discountPercent && (
                        <DiscountBadge percent={discountPercent} />
                      )}
                    </div>

                    <h4 className={styles.productTitle}>
                      {getProductDisplayName(product)}
                    </h4>
                  </Link>

                  <ProductRatingSummary product={product} />

                  <button
                    type="button"
                    className={`${styles.wishlistHeartButton} ${
                      isInWishlist(product._id)
                        ? styles.activeWishlistButton
                        : ""
                    }`}
                    onClick={() =>
                      isInWishlist(product._id)
                        ? removeFromWishlist(product._id)
                        : addToWishlist(product)
                    }
                    aria-label={
                      isInWishlist(product._id)
                        ? "Видалити з обраного"
                        : "Додати в обране"
                    }
                    aria-pressed={isInWishlist(product._id)}
                  >
                    {isInWishlist(product._id) ? (
                      <FaHeart aria-hidden="true" />
                    ) : (
                      <PiHeart aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    className={`${styles.wishlistHeartButton} ${styles.comparisonButton} ${
                      isInComparison(product._id)
                        ? styles.activeComparisonButton
                        : ""
                    }`}
                    onClick={() => toggleComparison(product)}
                    aria-label={
                      isInComparison(product._id)
                        ? "Видалити з порівняння"
                        : "Додати до порівняння"
                    }
                    aria-pressed={isInComparison(product._id)}
                    title={
                      isInComparison(product._id)
                        ? "Видалити з порівняння"
                        : "Додати до порівняння"
                    }
                  >
                    <PiScales aria-hidden="true" />
                  </button>

                  <div className={styles.cardBottomCentered}>
                    {isOutOfStock ? (
                      <div className={styles.outOfStockCardState}>
                            {formatPrice(regularPrice)} ₴
                        Немає в наявності
                      </div>
                    ) : (
                      <>
                        <div className={styles.priceWrapperCentered}>
                          {discountPercent && regularPrice > discountPrice && (
                            <span className={styles.oldPrice}>
                              {formatPrice(regularPrice)} ₴
                            </span>
                          )}
                          <span
                            className={
                              discountPercent
                                ? styles.discountPrice
                                : styles.productPrice
                            }
                          >
                            {formatPrice(currentPrice)} ₴
                          </span>
                        </div>

                        <button
                          type="button"
                          className={styles.addToCartIconButtonBottom}
                          onClick={() => {
                            addToCart(product, 1);
                            setSuccessMessage("Товар додано до кошика!");
                            setTimeout(() => setSuccessMessage(""), 3000);
                          }}
                          title="Додати в кошик"
                          aria-label="Додати в кошик"
                        >
                          <BsBasket aria-hidden="true" />
                        </button>
                      </>
                    )}
                  </div>
                </article>
              );
            })
            )}

            {/* Пагінація */}
            {!isProductsLoading && totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageButton}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  ←
                </button>

                {getPageNumbers().map((n, i) =>
                  n === "dots" ? (
                    <span key={`dots-${i}`} className={styles.pageButton}>
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={n}
                      className={`${styles.pageButton} ${
                        currentPage === n ? styles.activePage : ""
                      }`}
                      onClick={() => setCurrentPage(n)}
                    >
                      {n}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className={styles.pageButton}
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Мобільна бічна панель фільтрів */}
      {isMobile && isFilterSidebarOpen && (
        <div
          className={styles.mobileFilterOverlay}
          onClick={() => setIsFilterSidebarOpen(false)}
        >
          <div
            className={styles.mobileFilterSheet}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.mobileFilterSheetHeader}>
              <h2>Фільтри</h2>
              <button
                type="button"
                onClick={() => setIsFilterSidebarOpen(false)}
                aria-label="Закрити фільтри"
              >
                ×
              </button>
            </div>

            <div className={styles.mobileFilterContent}>
              <FilterSidebar
                isOpen={isFilterSidebarOpen}
                onClose={() => setIsFilterSidebarOpen(false)}
                products={filtersSourceProducts}
                kinds={kinds}
                filters={filters}
                animalCategory={animalCategory}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                visibleCategories={searchRelevantCategories}
                sheetMode
                hideInternalReset
              />
            </div>

            <div className={styles.mobileFilterActions}>
              <button type="button" onClick={handleResetFilters}>
                Скинути фільтри
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
